/**
 * Transformers to normalize Relay-style connection responses to array format
 * for use by registry components and services.
 */
import type { GetAllArtifactsQuery } from '@fly/store';
import type { ResultOf, VariablesOf } from '@graphql-typed-document-node/core';

import type {
  getArtifactDetailsQuery,
  getArtifactFilesQuery,
  getRegistryInsightsQuery,
  getRepositoriesQuery,
  getRepositoryArtifactsQuery,
  getRepositoryNamesQuery,
  getRepositoryReleaseQuery,
  getRepositoryReleasesQuery,
  getRepositoryWorkflowsQuery,
  getWorkflowNamesQuery,
} from './registry.queries';

type GetRepositoryNamesResponse = ResultOf<typeof getRepositoryNamesQuery>;
type GetRepositoriesResponse = ResultOf<typeof getRepositoriesQuery>;
type GetRegistryInsightsResponse = ResultOf<typeof getRegistryInsightsQuery>;
type GetRepositoryArtifactsResponse = ResultOf<
  typeof getRepositoryArtifactsQuery
>;
type GetRepositoryWorkflowsResponse = ResultOf<
  typeof getRepositoryWorkflowsQuery
>;
type GetRepositoryReleaseResponse = ResultOf<typeof getRepositoryReleaseQuery>;
type GetAllArtifactsResponse = GetAllArtifactsQuery;
type GetArtifactDetailsResponse = ResultOf<typeof getArtifactDetailsQuery>;

export const transformRepositoryNames = (
  response: GetRepositoryNamesResponse
) => {
  if (!response?.flyGitRepositories?.searchRepositories?.edges) {
    return [];
  }

  return response.flyGitRepositories.searchRepositories.edges.map(
    (edge) => edge.node
  );
};

export const transformRepositories = (response: GetRepositoriesResponse) => {
  if (!response?.flyGitRepositories?.searchRepositories?.edges) {
    return [];
  }

  return response.flyGitRepositories.searchRepositories.edges.map((edge) => ({
    ...edge.node,
    id: `${edge.node.owner}-${edge.node.name}`,
    configuredWorkflowsCount: edge.node.configuredWorkflowsCount,
    // Flatten artifacts from connection to array
    artifacts:
      edge.node.artifacts?.edges?.map((e) => ({
        ...e.node,
        latestVersion: e.node.latestVersion || {
          name: '',
          lastUploadTimestamp: null,
        },
      })) || [],
    latestArtifact: edge.node.latestArtifact ?? {
      name: '',
      uploadedLocally: false,
      lastUploadTimestamp: null,
    },
  }));
};

export const transformRegistryInsights = (
  response: GetRegistryInsightsResponse
) => {
  return (
    response?.flyOperationalInfo?.getRegistryInsights ?? {
      repositoriesCount: 0,
      repositoriesConnected: 0,
    }
  );
};

export const transformRepositoryArtifacts = (
  response: GetRepositoryArtifactsResponse
) => {
  const repo = response?.flyGitRepositories?.getRepository;
  if (!repo)
    return {
      name: '',
      owner: '',
      url: '',
      packageManagers: [] as string[],
      artifacts: [],
    };

  // Transform artifacts from connection to array
  const artifacts =
    repo.artifacts?.edges?.map((edge) => ({
      ...edge.node,
      latestVersion: {
        ...edge.node.latestVersion,
        releases:
          edge.node.latestVersion?.releases?.edges?.map(
            (runEdge) => runEdge.node
          ) || [],
      },
    })) || [];

  // Sort artifacts by last upload timestamp
  const sortedArtifacts = [...artifacts].sort((a, b) => {
    return (
      new Date(b.latestVersion.lastUploadTimestamp || 0).getTime() -
      new Date(a.latestVersion.lastUploadTimestamp || 0).getTime()
    );
  });

  return {
    ...repo,
    artifacts: sortedArtifacts,
  };
};

export const transformRepositoryWorkflows = (
  response: GetRepositoryWorkflowsResponse
) => {
  const repo = response?.flyGitRepositories?.getRepository;
  if (!repo) return { name: '', owner: '', url: '', workflows: [] };

  // Transform workflows from connection to array
  const workflows = repo.workflows?.edges?.map((edge) => edge.node) || [];

  // Sort by ciConfigured first, then alphabetically
  const sortedWorkflows = [...workflows].sort((a, b) => {
    if (a.ciConfigured !== b.ciConfigured) {
      return a.ciConfigured ? -1 : 1;
    }
    return a.name.localeCompare(b.name);
  });

  return {
    ...repo,
    workflows: sortedWorkflows,
  };
};

export const transformRepositoryRelease = (
  response: GetRepositoryReleaseResponse
) => {
  const repo = response?.flyGitRepositories?.getRepository;
  if (!repo) return [];

  // Transform workflows from connection
  return (
    repo.workflows?.edges?.map((workflowEdge) => ({
      ...workflowEdge.node,
      releases:
        workflowEdge.node.releases?.edges?.map((runEdge) => ({
          ...runEdge.node,
          pullRequests:
            runEdge.node.pullRequests?.edges?.map((prEdge) => prEdge.node) ||
            [],
          commits:
            runEdge.node.commits?.edges?.map((commitEdge) => commitEdge.node) ||
            [],
          uploadedArtifacts:
            runEdge.node.uploadedArtifacts?.edges?.map((artEdge) => ({
              ...artEdge.node,
              versions:
                artEdge.node.versions?.edges?.map(
                  (versionEdge) => versionEdge.node
                ) || [],
            })) || [],
        })) || [],
    })) || []
  );
};

export type RepositoryReleaseResult = ReturnType<
  typeof transformRepositoryRelease
>;

export const transformArtifacts = (response: GetAllArtifactsResponse) => {
  if (!response?.flyArtifacts?.searchArtifacts?.edges) {
    return [];
  }

  return response.flyArtifacts.searchArtifacts.edges
    .map((edge) => edge.node)
    .sort((a, b) => {
      return (
        new Date(b.lastUploadTimestamp || 0).getTime() -
        new Date(a.lastUploadTimestamp || 0).getTime()
      );
    });
};

export type VersionsPageInfo = {
  hasNextPage: boolean;
  endCursor: string | null;
};

export const transformArtifactDetails = (
  response: GetArtifactDetailsResponse
) => {
  if (!response?.flyArtifacts?.searchArtifacts?.edges) {
    return [];
  }

  return response.flyArtifacts.searchArtifacts.edges.map((edge) => {
    const versionsConnection = edge.node.versions;
    const versions =
      versionsConnection?.edges?.map((versionEdge) => ({
        ...versionEdge.node,
        releases: versionEdge.node.releases?.edges?.map((e) => e.node) ?? [],
      })) || [];
    const versionsPageInfo: VersionsPageInfo | undefined =
      versionsConnection?.pageInfo
        ? {
            hasNextPage: versionsConnection.pageInfo.hasNextPage ?? false,
            endCursor: versionsConnection.pageInfo.endCursor ?? null,
          }
        : undefined;
    return {
      ...edge.node,
      versions,
      ...(versionsPageInfo != null && { versionsPageInfo }),
    };
  });
};

export type GetArtifactFilesResponse = ResultOf<typeof getArtifactFilesQuery>;
export type GetArtifactFilesResult = ReturnType<typeof transformArtifactFiles>;
export type GetArtifactFilesVariables = VariablesOf<
  typeof getArtifactFilesQuery
>;
export type ArtifactFile = GetArtifactFilesResult['files'][number];

export const transformArtifactFiles = (response: GetArtifactFilesResponse) => {
  const distributedFiles = response?.flyArtifacts?.searchDistributedFiles;
  if (!distributedFiles?.edges) {
    return {
      files: [],
      pageInfo: {
        hasNextPage: false,
        endCursor: null,
      },
      totalCount: 0,
    };
  }

  const pageInfo = distributedFiles.pageInfo ?? {
    hasNextPage: false,
    endCursor: null,
  };

  return {
    files: distributedFiles.edges.map((edge) => edge.node),
    pageInfo,
    totalCount: distributedFiles.totalCount ?? 0,
  };
};

// -- Releases

export type GetRepositoryReleasesResponse = ResultOf<
  typeof getRepositoryReleasesQuery
>;
export type GetRepositoryReleasesVariables = VariablesOf<
  typeof getRepositoryReleasesQuery
>;

export type GetRepositoryReleasesResult = ReturnType<
  typeof transformRepositoryReleases
>;

export type RepositoryRelease = GetRepositoryReleasesResult['releases'][number];

export const transformRepositoryReleases = (
  response: GetRepositoryReleasesResponse
) => {
  const releases = response?.flyReleases?.getReleases;
  if (!releases?.edges) {
    return {
      releases: [],
      pageInfo: { hasNextPage: false, endCursor: null },
      totalCount: 0,
      uploaders: [],
      environments: [],
    };
  }

  return {
    releases: releases.edges.map((edge) => edge.node),
    pageInfo: releases.pageInfo,
    totalCount: releases.totalCount,
    uploaders: releases.uploaders ?? [],
    environments: releases.environments ?? [],
  };
};

// -- Workflow Names

export type GetWorkflowNamesResponse = ResultOf<typeof getWorkflowNamesQuery>;
export type GetWorkflowNamesVariables = VariablesOf<
  typeof getWorkflowNamesQuery
>;

export type GetWorkflowNamesResult = ReturnType<typeof transformWorkflowNames>;

export type WorkflowName = GetWorkflowNamesResult[number];

export const transformWorkflowNames = (response: GetWorkflowNamesResponse) => {
  return response.flyReleases.searchWorkflowNames;
};
